import { useMemo } from "react"
import { Column, TableRecordType } from "../types"
import { NoDataType } from "../constants"
import { ReactElement } from "react"

type UseTableLogicParams = {
  records: TableRecordType[]
  loading?: boolean
  headerColumns: Column[]
  displayedProperties?: string[]
  selectable?: boolean
  getRowAction?: (
    id: string,
    record?: { [key: string]: string }
  ) => ReactElement
  hasActiveFilters?: boolean
  searchString?: string
  onSelectDisplayedProperty?: (name: string) => void
}

export const useTableLogic = ({
  records,
  loading,
  headerColumns,
  displayedProperties,
  selectable = false,
  getRowAction,
  hasActiveFilters = false,
  searchString,
  onSelectDisplayedProperty
}: UseTableLogicParams) => {
  const isEmpty = !records.length && !loading

  const totalColumnsCount = useMemo(() => {
    return (
      (displayedProperties ?? headerColumns).length +
      1 +
      Number(selectable) +
      Number(!!getRowAction)
    )
  }, [displayedProperties, headerColumns, selectable, getRowAction])

  const emptyPlaceholderType = useMemo(() => {
    if (hasActiveFilters && !searchString) {
      return NoDataType.NO_DATA_BY_FILTERS
    }
    if (!!searchString) {
      return NoDataType.NO_DATA_BY_SEARCH
    }
    return NoDataType.NO_DATA
  }, [hasActiveFilters, searchString])

  const visibleColumns = useMemo(() => {
    if (!displayedProperties) {
      return headerColumns
    }
    return headerColumns.filter(col => displayedProperties.includes(col.name))
  }, [headerColumns, displayedProperties])

  const dropdownOptions = useMemo(() => {
    if (!displayedProperties || !onSelectDisplayedProperty) {
      return []
    }

    const autoGeneratedOptions = headerColumns.map(
      ({ label, name, columns: childColumns }) => {
        // GROUPED COLUMN - Create collapsible header
        if (childColumns && childColumns.length > 0) {
          return {
            id: name,
            collapsibleHeader: true,
            label: label,
            children: childColumns.map(child => ({
              id: child.name,
              label: child.label,
              selected: displayedProperties.includes(child.name),
              action: () => onSelectDisplayedProperty(child.name),
              "data-automation-id": "data-table-head-menu-item"
            }))
          }
        }

        // REGULAR COLUMN - Create checkbox
        return {
          id: name,
          label: label,
          selected: displayedProperties.includes(name),
          action: () => onSelectDisplayedProperty(name),
          "data-automation-id": "data-table-head-menu-item"
        }
      }
    )

    return autoGeneratedOptions
  }, [headerColumns, displayedProperties, onSelectDisplayedProperty])

  return {
    isEmpty,
    totalColumnsCount,
    emptyPlaceholderType,
    visibleColumns,
    dropdownOptions
  }
}
